import React from "react";
import { Skeleton } from "antd";
import styled from "styled-components";

import { TD } from "./tokens";
import { ReturningWrapper } from "./Returning.style";

/*
 * Loading shimmer for the dashboard route, before the app knows which teacher screen to show.
 *
 * A teacher on their first login gets the full-screen First Login page (no side menu, no header),
 * so while they wait they get a plain full-screen shimmer in the same cream colour, not the CMS
 * shell with the side menu. A teacher who has already seen the Returning dashboard in this browser
 * gets the plain shimmer inside the CMS shell, as before.
 *
 * Kept outside the lazily loaded TeacherDashboard chunk so DashboardRouter can show it too.
 */

const RETURNING_KEY = "td_returning_seen";

/** Who is logged in, from the access token (falls back to one key per browser). */
function userKey() {
	try {
		const token = sessionStorage.getItem("cms_accessToken") || "";
		const payload = JSON.parse(window.atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
		return String(payload.sub || payload.userId || payload.id || payload.email || "any");
	} catch (e) {
		return "any";
	}
}

/** True when this user has already opened the Returning dashboard in this browser. */
export function isKnownReturning() {
	try {
		const seen = JSON.parse(localStorage.getItem(RETURNING_KEY) || "[]");
		return Array.isArray(seen) && seen.includes(userKey());
	} catch (e) {
		return false;
	}
}

/** Called when the Returning dashboard is shown, so later visits get the shell shimmer. */
export function rememberReturning() {
	try {
		const key = userKey();
		const seen = JSON.parse(localStorage.getItem(RETURNING_KEY) || "[]");
		const list = Array.isArray(seen) ? seen : [];
		if (!list.includes(key)) localStorage.setItem(RETURNING_KEY, JSON.stringify([...list, key].slice(-20)));
	} catch (e) {
		// storage blocked: the full-screen shimmer is shown instead, which is harmless
	}
}

export function CardSkeleton() {
	return (
		<section className="card">
			<Skeleton active paragraph={{ rows: 4 }} />
		</section>
	);
}

/** Same rows as the loaded screen, drawn with the CMS's shimmer (antd Skeleton). */
export function CardRowsSkeleton() {
	return (
		<>
			<div className="row row-wide">
				<CardSkeleton />
				<CardSkeleton />
			</div>
			<div className="row row-wide">
				<CardSkeleton />
				<CardSkeleton />
			</div>
			<div className="row row-three">
				<CardSkeleton />
				<CardSkeleton />
				<CardSkeleton />
			</div>
		</>
	);
}

/** Whole-screen shimmer shown until the first API response arrives. */
export function ReturningSkeleton() {
	return (
		<ReturningWrapper>
			<div className="stack">
				<section className="card">
					<Skeleton active avatar paragraph={{ rows: 2 }} />
				</section>
				<CardRowsSkeleton />
				<CardSkeleton />
			</div>
		</ReturningWrapper>
	);
}

const Cover = styled.div`
	position: fixed;
	inset: 0;
	z-index: 999; /* same layer as the First Login screen: covers the side menu and header */
	overflow: hidden;
	background: ${TD.cream};
	.bar {
		height: 72px;
		background: ${TD.white};
		border-bottom: 1px solid ${TD.creamBorder};
	}
	.page {
		max-width: 1200px;
		margin: 0 auto;
		padding: 48px 24px;
		display: flex;
		flex-direction: column;
		gap: 32px;
	}
	.cards {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 24px;
		@media (max-width: 900px) {
			grid-template-columns: 1fr;
		}
	}
	.card {
		padding: 24px;
		background: ${TD.white};
		border: 1px solid ${TD.creamBorder};
		border-radius: 20px;
	}
`;

/**
 * mode "first": full-screen cream shimmer (first login, or not known yet);
 * "returning": the Returning dashboard's own shimmer, so a returning teacher sees one shimmer only;
 * "plain": simple shimmer inside the CMS shell (admin / school users).
 */
export default function LoadingShimmer({ mode = "plain" }) {
	if (mode === "returning") return <ReturningSkeleton />;
	if (mode !== "first") {
		return (
			<div style={{ padding: 24 }}>
				<Skeleton active paragraph={{ rows: 10 }} />
			</div>
		);
	}
	return (
		<Cover aria-busy="true" aria-label="Loading">
			<div className="bar" />
			<div className="page">
				<Skeleton active title={{ width: "40%" }} paragraph={{ rows: 3, width: ["70%", "60%", "50%"] }} />
				<div className="cards">
					{[0, 1, 2].map((key) => (
						<div className="card" key={key}>
							<Skeleton active paragraph={{ rows: 4 }} />
						</div>
					))}
				</div>
			</div>
		</Cover>
	);
}

/**
 * Shimmer for the dashboard route while its code is still downloading (the route's loading
 * fallback). Uses only what the browser already knows, so a first-time teacher gets the
 * full-screen shimmer from the very first frame instead of the CMS table shimmer.
 */
export function DashboardRouteFallback() {
	let teacherPortal = false;
	try {
		const role = sessionStorage.getItem("cms_role");
		teacherPortal = role === "teacher" || (!role && sessionStorage.getItem("cms_loginPortal") === "teacher");
	} catch (e) {
		teacherPortal = false;
	}
	return <LoadingShimmer mode={teacherShimmerMode(teacherPortal)} />;
}

/** Which shimmer a teacher-portal user gets before the app knows their screen. */
export function teacherShimmerMode(teacherPortal) {
	if (!teacherPortal) return "plain";
	return isKnownReturning() ? "returning" : "first";
}
